import { useCallback, useEffect, useState } from 'react';
import {
  Text,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Divider,
  useColorMode,
  useColorStyle,
  Flex,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import DetectionsTable from '../DetectionsTable';
import CustomTab from './CustomTab';
import { useQuarantineStore } from '@Quarantine/store';
import { RiskAiRiskType } from '../types';
import { useLocation, useSearchParams } from 'react-router-dom';
import { OperationTabUrl } from '@Quarantine/utils/constants';

const DetectionsTabs = (props: { filterInitialized: boolean }) => {
  const { filterInitialized } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { setFilter } = useQuarantineStore((state) => state.riskAi);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [tab, setTab] = useState(resolveCurrentTab(searchParams));

  const dividerColor = colorStyle.background.tertiary;

  const handleTabChange = useCallback(
    (riskType: RiskAiRiskType) => {
      if (location.pathname !== OperationTabUrl.RiskAi) return;
      setFilter('riskTypeFilter', riskType);
      searchParams.set('riskType', riskType);
      setSearchParams(searchParams);
      setTab(riskType);
    },
    [location.pathname, searchParams, setFilter, setSearchParams]
  );

  useEffect(() => {
    handleTabChange(resolveCurrentTab(searchParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTabChange]);

  return (
    <Tabs flex="1 1 auto" index={tab}>
      <TabList px="4x" mt="4x">
        <CustomTab
          key={0}
          index={RiskAiRiskType.SecurityRisk}
          onClick={() => handleTabChange(RiskAiRiskType.SecurityRisk)}
        >
          <Text>{t('riskAi.tab.threat')}</Text>
        </CustomTab>
        <CustomTab
          key={1}
          index={RiskAiRiskType.Anomaly}
          onClick={() => handleTabChange(RiskAiRiskType.Anomaly)}
        >
          <Text>{t('riskAi.tab.anomaly')}</Text>
        </CustomTab>
        <CustomTab
          key={2}
          index={RiskAiRiskType.CustomizedAnomaly}
          onClick={() => handleTabChange(RiskAiRiskType.CustomizedAnomaly)}
        >
          <Text>{t('riskAi.tab.customizedAnomaly')}</Text>
        </CustomTab>
      </TabList>
      <TabPanels display="flex" flexDirection="column" flex="1 1 auto">
        <TabPanel index={RiskAiRiskType.SecurityRisk}>
          {({ isSelected }: { isSelected: boolean }) =>
            isSelected && (
              <>
                <Text p="4x">{t('riskAi.threat.description')}</Text>
                <Divider backgroundColor={dividerColor} />
                <Flex flexDirection="column" flex="1 1 auto">
                  <DetectionsTable filterInitialized={filterInitialized} />
                </Flex>
              </>
            )
          }
        </TabPanel>
        <TabPanel index={RiskAiRiskType.Anomaly}>
          {({ isSelected }: { isSelected: boolean }) =>
            isSelected && (
              <>
                <Text p="4x">{t('riskAi.anomaly.description')}</Text>
                <Divider backgroundColor={dividerColor} />
                <Flex flexDirection="column" flex="1 1 auto">
                  <DetectionsTable filterInitialized={filterInitialized} />
                </Flex>
              </>
            )
          }
        </TabPanel>
        <TabPanel index={RiskAiRiskType.CustomizedAnomaly}>
          {({ isSelected }: { isSelected: boolean }) =>
            isSelected && (
              <>
                <Text p="4x">{t('riskAi.anomaly.description')}</Text>
                <Divider backgroundColor={dividerColor} />
                <Flex flexDirection="column" flex="1 1 auto">
                  <DetectionsTable filterInitialized={filterInitialized} />
                </Flex>
              </>
            )
          }
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default DetectionsTabs;

function resolveCurrentTab(searchParams: URLSearchParams) {
  // Set the default filter value to the riskType parameter in the URL otherwise set it to SecurityRisk
  let riskType = searchParams.has('riskType')
    ? (searchParams.get('riskType') as RiskAiRiskType)
    : RiskAiRiskType.SecurityRisk;
  if (!Object.values(RiskAiRiskType).includes(riskType)) {
    riskType = RiskAiRiskType.SecurityRisk;
  }
  return riskType;
}
